import { useState, useEffect, useCallback } from 'react';
import { logger } from '../../shared/logger';
import { inviteApi } from '../../api';
import { InviteCode } from '../../api_types';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

interface InviteCodeGeneratorProps {
  facilityId: string;
  businessId: string;
}

export default function InviteCodeGenerator({ facilityId, businessId }: InviteCodeGeneratorProps) {
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courtsReady, setCourtsReady] = useState(false);
  const [paymentsReady, setPaymentsReady] = useState(false);
  const [plansReady, setPlansReady] = useState(false);
  const [setupChecked, setSetupChecked] = useState(false);

  // New state for email invite
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState('');

  const createInviteCode = async () => {
    try {
      setLoading(true);
      setError('');
      
      await inviteApi.createInviteCode(businessId, facilityId);
      await fetchCodes();
    } catch (err: unknown) {
      logger.error('Failed to create invite code', err instanceof Error ? err : { error: String(err) });
      setError('Failed to create invite code');
    } finally {
      setLoading(false);
    }
  };

  const fetchCodes = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedCodes = await inviteApi.getInviteCodes(businessId, facilityId);
      setCodes(fetchedCodes);
    } catch (err: unknown) {
      logger.error('Failed to fetch invite codes', err instanceof Error ? err : { error: String(err) });
      setError('Failed to fetch invite codes');
    } finally {
      setLoading(false);
    }
  }, [facilityId, businessId]);

  const deleteCode = async (codeId: string) => {
    try {
      setLoading(true);
      await inviteApi.deleteInviteCode(businessId, facilityId, codeId);
      await fetchCodes();
    } catch (err: unknown) {
      logger.error('Failed to delete invite code', err instanceof Error ? err : { error: String(err) });
      setError('Failed to delete invite code');
    } finally {
      setLoading(false);
    }
  };

  // Check courts, payments, and plans setup
  useEffect(() => {
    const checkSetup = async () => {
      try {
        setLoading(true);
        const setupStatus = await inviteApi.getFacilitySetupStatus(businessId, facilityId);
        setCourtsReady(setupStatus.has_courts);
        setPaymentsReady(setupStatus.has_stripe);
        setPlansReady(setupStatus.has_plans);
      } catch (err) {
        console.error('Error checking setup:', err);
        setCourtsReady(false);
        setPaymentsReady(false);
        setPlansReady(false);
      } finally {
        setSetupChecked(true);
        setLoading(false);
      }
    };
    checkSetup();
  }, [facilityId, businessId]);

  // Fetch codes when component mounts
  useEffect(() => {
    if (setupChecked && courtsReady && paymentsReady && plansReady) {
      fetchCodes();
    }
  }, [fetchCodes, setupChecked, courtsReady, paymentsReady, plansReady]);

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setSendError('');
    setSendSuccess('');

    try {
      const response = await inviteApi.sendInvite({
        email: inviteEmail,
        facility_id: facilityId,
        business_id: businessId,
        is_staff: false
      });

      if (response.success) {
        setSendSuccess('Invite sent successfully!');
        setInviteEmail('');
        await fetchCodes(); // Refresh the list of codes
      } else {
        setSendError(response.message || 'Failed to send invite');
      }
    } catch (error: any) {
      setSendError(error.message || 'Failed to send invite');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Block the page if requirements are not met
  if (!courtsReady || !paymentsReady || !plansReady) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2 mb-4">Member Invite Codes</h2>
        <div className="space-y-4">
          <div className="flex items-center">
            <div className={`flex-shrink-0 h-5 w-5 ${courtsReady ? 'text-green-500' : 'text-red-500'}`}>
              {courtsReady ? (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${courtsReady ? 'text-green-800' : 'text-red-800'}`}>
                Courts Setup
              </h3>
              {!courtsReady && (
                <p className="mt-1 text-sm text-red-600">
                  Please add at least one court to your facility before generating invite codes.
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <div className={`flex-shrink-0 h-5 w-5 ${paymentsReady ? 'text-green-500' : 'text-red-500'}`}>
              {paymentsReady ? (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${paymentsReady ? 'text-green-800' : 'text-red-800'}`}>
                Stripe Payments Setup
              </h3>
              {!paymentsReady && (
                <p className="mt-1 text-sm text-red-600">
                  Please connect your Stripe account to enable payments before generating invite codes.
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <div className={`flex-shrink-0 h-5 w-5 ${plansReady ? 'text-green-500' : 'text-red-500'}`}>
              {plansReady ? (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <div className="ml-3">
              <h3 className={`text-sm font-medium ${plansReady ? 'text-green-800' : 'text-red-800'}`}>
                Subscription Plans Setup
              </h3>
              {!plansReady && (
                <p className="mt-1 text-sm text-red-600">
                  Please create at least one subscription plan before generating invite codes.
                </p>
              )}
            </div>
          </div>

          <div className="mt-6 bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Setup Required
                </h3>
                <p className="mt-2 text-sm text-yellow-700">
                  Complete the above requirements to start generating invite codes for your members.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2 mb-4">Member Invite Codes</h2>
        <button
          onClick={createInviteCode}
          disabled={loading || !courtsReady || !paymentsReady || !plansReady}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
        >
          Generate New Code
        </button>
      </div>

      {error && (
        <div className="mb-4 text-sm text-red-600">{error}</div>
      )}

      <div className="mb-6">
        <form onSubmit={handleSendInvite} className="flex gap-2 items-center">
          <input
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            placeholder="Enter email address"
            required
            className="border rounded px-2 py-1"
          />
          <button
            type="submit"
            disabled={sending}
            className="bg-indigo-600 text-white px-4 py-1 rounded"
          >
            {sending ? 'Sending...' : 'Send Invite'}
          </button>
          {sendError && <span className="text-red-600 ml-2">{sendError}</span>}
          {sendSuccess && <span className="text-green-600 ml-2">{sendSuccess}</span>}
        </form>
      </div>

      <div className="mt-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {codes.map((code) => (
              <tr key={code.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{code.code}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(code.createdAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(code.expiresAt).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {code.isUsed ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      Used
                    </span>
                  ) : new Date(code.expiresAt) < new Date() ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                      Expired
                    </span>
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Active
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button
                    onClick={() => deleteCode(code.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
} 